// Preseason win projections — the "over/under" line for the season report card.
//
// Turns each team's preseason overall rating into a projected regular-season
// win total. The map is deliberately simple and self-balancing: projections are
// spread linearly around the LEAGUE-MEAN ovr, so a team's line reflects its
// strength relative to the field, and the projected wins sum to the total games
// played (sum of (ovr - mean) is zero). This keeps the field internally
// consistent no matter how compressed or spread the league's ovrs happen to be.
//
// The one free parameter, WINS_PER_OVR, controls how much one ovr point above
// the league mean is worth in wins. It is derived from the codebase's own
// ovr<->margin relationship (see core/team/ovr.basketball.ts, where ovr encodes
// a predicted margin of victory) combined with the standard basketball rule of
// thumb of ~2.7 wins per point of season margin. It is PROVISIONAL and should
// be recalibrated against real simulated win distributions in Phase 3.

// Wins per ovr point above the league mean, per an 82-game season. Scaled by
// numGames for other league lengths.
export const WINS_PER_OVR = 1;

// Clamp projections to a realistic band so a lopsided league can't produce a
// 0-win or every-game-won line.
const MIN_WIN_FRACTION = 0.1;
const MAX_WIN_FRACTION = 0.9;

const clamp = (x: number, lo: number, hi: number) =>
	Math.max(lo, Math.min(hi, x));

// Round to the nearest half win, for a Vegas-style over/under line (e.g. 47.5)
// that the actual, integer win total can never exactly "push" against.
const roundToHalf = (x: number) => Math.round(x * 2) / 2;

// Given every team's preseason ovr (in one array) and the season length, return
// each team's projected win total in the SAME order.
export const projectWins = (
	teamOvrs: number[],
	numGames: number,
): number[] => {
	const n = teamOvrs.length;
	if (n === 0) {
		return [];
	}

	const meanOvr = teamOvrs.reduce((sum, ovr) => sum + ovr, 0) / n;
	const winsPerOvr = WINS_PER_OVR * (numGames / 82);
	// Half-win bounds so clamped results stay on half-win lines.
	const lo = roundToHalf(numGames * MIN_WIN_FRACTION);
	const hi = roundToHalf(numGames * MAX_WIN_FRACTION);

	return teamOvrs.map((ovr) => {
		const raw = numGames / 2 + (ovr - meanOvr) * winsPerOvr;
		return clamp(roundToHalf(raw), lo, hi);
	});
};

export default projectWins;
